<template>
  <div class="relative flex flex-col justify-between h-full bg-black">
    <!-- Logo -->
    <div class="items-center hidden h-16 p-4 text-white border-b border-gray-900 lg:flex">
      <img src="../assets/img/logo-light.svg" class="h-8">
    </div>

    <!-- Account Select Modal -->
    <Modal v-if="user" title="Select Account" :open.sync="showAccountSelectModal">
      <div class="p-4 overflow-y-scroll bg-white border-t h-96 " v-if="user && user.accounts">
        <button class="flex items-center w-full p-4 font-medium text-gray-800 rounded cursor-pointer hover:bg-gray-100" v-for="(account, index) in user.accounts" :key="index" @click="changeAccount(account)">
          <div class="relative flex items-center justify-center mr-4">
            <div class="w-5 h-5 rounded-full opacity-50" :style="computeBackgroundColor(account.color)"></div>
            <div class="absolute w-3 h-3 rounded-full opacity-100" :style="computeBackgroundColor(account.color)"></div>
          </div> 
          <div class="leading-tight">
            <div class="font-bold">{{ account.name }}</div>
          </div>
        </button>
      </div>
      <div class="p-4 border-t" v-if="user.type === 'admin' || user.type === 'partner'">
        <router-link class="btn btn-primary" :to="{ name: 'OnboardingCompany' }">
          <fa :icon="['far', 'plus-circle']" class="mr-2" /> Create New Account
        </router-link>
      </div>
    </Modal>

    <!-- Menu -->
    <div class="flex-grow overflow-y-auto" v-if="account && user">
      <!-- Account Selector -->
      <div class="p-2" v-if="user && user.accounts && user.accounts.length > 0">
        <button class="relative flex items-center justify-between w-full text-left text-gray-200 transition duration-150 bg-gray-900 border border-gray-900 rounded hover:bg-gray-800" :disabled="user.accounts.length < 2" @click="showAccountSelectModal = true">
          <div class="flex items-center flex-grow max-w-full px-3 py-2">
            <div class="relative flex items-center justify-center mr-4">
              <div class="w-5 h-5 rounded-full opacity-50" :style="computeBackgroundColor(account.color)"></div>
              <div class="absolute w-3 h-3 rounded-full opacity-100" :style="computeBackgroundColor(account.color)"></div>
            </div> 
            <div class="flex-grow pr-6 font-medium truncate whitespace-nowrap">
              {{ account.name }}
            </div>
          </div>
          <div class="absolute right-0 px-4 py-2 border-gray-800 rounded-r">
            <fa :icon="['far', 'chevron-right']" size="sm" /> 
          </div>
        </button>
      </div>

      <!-- Menu -->
      <template v-if="account">
        <div v-for="link in allowedLinks" :key="link.icon">
          <button 
            @click="toggleAccordion(link)"
            class="flex items-center w-full px-4 py-3 font-medium tracking-wide text-gray-200 border-l border-transparent hover:text-white hover:no-underline group" 
            active-class="text-white bg-gray-900 border-l border-indigo-500"
            :class="{ 
              'active': link.open && $route.name === link.route,
              'pointer-events-none opacity-25': !hasPermission(account.permissions, link.permissions) 
              }">
              <fa :icon="['fad', link.icon]" fixed-width class="mr-5 text-lg text-gray-400 group-hover:text-gray-300" />
              <div class="text-base">{{ link.name }}</div>
              <fa :icon="['far', 'chevron-down']" class="ml-auto text-xs transition duration-150 opacity-50" :class="{ 'transform rotate-180': link.open }" v-if="link.links" />
          </button>
          <Collapse v-if="link.links" :active="link.open" :duration="200" class="py-1 bg-gray-800 bg-opacity-25">
            <router-link 
              v-for="sublink in link.links" 
              :to="{ name: sublink.route, params: { account: account.subdomain } }" 
              :key="sublink.route" 
              class="flex items-center py-3 text-sm font-medium border-l border-transparent opacity-75 text-gray-50 hover:opacity-100 focus:opacity-100 hover:no-underline focus:no-underline group"
              active-class="bg-gray-800 bg-opacity-50 border-l opacity-100 border-primary"
              :class="{ 
                'pointer-events-none opacity-25': !hasPermission(account.permissions, sublink.permissions) 
                }">
                <fa :icon="['fas', sublink.icon]" fixed-width class="ml-4 mr-5 text-base text-center opacity-50 group-hover:opacity-100" /> 
                <div>{{ sublink.name }}</div>
            </router-link>
          </Collapse>
        </div>
      </template>
    </div>
    
    <!-- User -->
    <div class="flex items-center p-4 text-gray-100 bg-gray-900" v-if="user">
      <avatar :username="user.name" :size="36" color="#fff" background-color="#718096" class="mr-4" :custom-style="{ 'font-weight': 'bold' }" /> 
      <div class="leading-none">
        <div class="text-sm font-medium">
          {{ user.name }}
        </div>
        <button class="text-xs font-medium text-indigo-500 hover:text-indigo-400" @click.prevent="logout">
          <fa :icon="['far', 'sign-out']" class="transform rotate-180" /> Logout
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import Avatar from 'vue-avatar'
  import { mapState, mapGetters } from 'vuex'

  export default {
    name: 'DashboardNav',

    components: {
      Avatar
    },

    props: {
      open: {
        type: Boolean
      }
    },

    computed: {
      ...mapState(['user', 'colors']),
      ...mapGetters(['account']),

      allowedLinks () {
        return this.links.filter((link) => {
          if (link.requires === null) { 
            return link
          } else if (this.account.subscription && this.account.subscription[link.requires]) {
            return link
          }
        })
      }
    },

    data () {
      return {
        showAccountSelectModal: false,
        showSettingsMenu: false,
        links: [
          { 
            icon: 'inbox-in', 
            name: 'Orders', 
            route: 'OrderList', 
            requires: 'orders',
            permissions: ['orders'],
            open: false,
            links: [
              { icon: 'concierge-bell', name: 'Approved Orders', route: 'OrderList', permissions: ['orders'] },
              { icon: 'calendar-day', name: 'Bookings', route: 'BookingList', permissions: ['orders'] },
              { icon: 'oven', name: 'Batch Upload', route: 'BatchUpload', permissions: ['orders'] }
            ] 
          },
          { 
            icon: 'users', 
            name: 'Customers', 
            route: 'CustomerList', 
            requires: 'orders',
            permissions: ['orders'],
            open: false,
            links: [
              { icon: 'address-card', name: 'Customers', route: 'CustomerList', permissions: ['orders'] },  
              { icon: 'map-marker-alt', name: 'Addresses', route: 'AddressList', permissions: ['orders'] }
            ] 
          },
          { 
            icon: 'bell-on', 
            name: 'Notifications', 
            route: 'NotificationSettings', 
            requires: 'orders',
            permissions: ['orders'],
            open: false,
            links: [
              { icon: 'paper-plane', name: 'Sent Items', route: 'NotificationList', permissions: ['orders'] },  
              { icon: 'sliders-h-square', name: 'Configuration', route: 'NotificationSettings', permissions: ['orders'] },  
            ] 
          },
          { 
            icon: 'browser', 
            name: 'Website', 
            route: 'BookingFormList', 
            requires: 'orders',
            permissions: ['orders'],
            open: false,
            links: [
              { icon: 'book', name: 'Booking Forms', route: 'BookingFormList', permissions: ['orders'] },
              { icon: 'boxes', name: 'Products', route: 'ProductList', permissions: ['orders'] },
              { icon: 'plus-square', name: 'Addons', route: 'AddonList', permissions: ['orders'] }
            ] 
          },
          { 
            icon: 'sync', 
            name: 'Automations', 
            route: 'AutomationList',
            requires: 'automations',
            permissions: ['automations'],
            open: false,
            links: [
              { icon: 'eye', name: 'Watchers', route: 'AutomationList', permissions: ['automations'] },
              { icon: 'address-book', name: 'Contacts', route: 'ContactList', permissions: ['automations'] },
              { icon: 'stream', name: 'Logs', route: 'LogList', permissions: ['automations'] },
              { icon: 'broadcast-tower', name: 'Monitor', route: 'Monitor', permissions: ['automations'] },
            ]
          },
          // {
          //   icon: 'signal-stream',
          //   name: 'Activity',
          //   route: 'ActivityList',
          //   requires: 'exceptions',
          //   links: [
          //     { icon: 'exclamation-circle', name: 'Exceptions', route: 'ExceptionList', permissions: ['automations'] },
          //     { icon: 'lightbulb-on', name: 'Insights', route: 'Insights', permissions: ['automations'] },
          //     { icon: 'comment-alt-dots', name: 'Reasons', route: 'AutomationList', permissions: ['automations'] },
          //   ]
          // },
          { 
            icon: 'books', 
            name: 'Records', 
            route: 'RecordList', 
            requires: 'records',
            permissions: ['records'],
            open: false,
            links: [
              { icon: 'receipt', name: 'Entries', route: 'RecordList', permissions: ['records'] },
              { icon: 'tag', name: 'Categories', route: 'CategoryList', permissions: ['records'] },
              { icon: 'copy', name: 'Files', route: 'FileList', permissions: ['records'] },
              { icon: 'store', name: 'Vendors', route: 'VendorList', permissions: ['records'] },
            ] 
          },
          { 
            icon: 'car-building', 
            name: 'Assets', 
            route: '', 
            requires: null,
            permissions: ['assets'],
            open: false,
            links: [
              { icon: 'vector-square', name: 'Areas', route: 'AreaList', permissions: ['assets'] },
              { icon: 'warehouse-alt', name: 'Depots', route: 'DepotList', permissions: ['assets'] },
              { icon: 'steering-wheel', name: 'Drivers', route: 'DriverList', permissions: ['assets'] },
              { icon: 'car', name: 'Vehicles', route: 'VehicleList', permissions: ['assets'], }
            ]
          },
          { 
            icon: 'route', 
            name: 'Routes', 
            route: 'RouteList', 
            requires: 'routes',
            permissions: ['reports'],
            open: false
          },
          { 
            icon: 'poll', 
            name: 'Reports', 
            route: 'ReportList',
            requires: null,
            permissions: ['reports'],
            open: false
          },
          { 
            icon: 'cog', 
            name: 'Settings', 
            route: 'Settings', 
            requires: null,
            permissions: ['account', 'billing', 'users'],
            open: false
          }
        ]
      }
    },

    methods: {
      async changeAccount (account) {
        if (account.subdomain === this.$route.params.account) {
          this.showAccountSelectModal = false
          return
        }
        
        this.$store.commit('setAccount', account.subdomain)
        this.$router.push({ params: { account: account.subdomain } })
        this.showAccountSelectModal = false
      },

      async logout() {
        await this.$store.commit('logout')
        this.$router.push({ name: 'Login' })
      },

      computeBackgroundColor (color) {
        return { background: `rgba(${this.colors[color]}, 1)` }
      },

      toggleAccordion (link) {
        if (!this.hasPermission(this.account.permissions, link.permissions)) {
          return
        }

        if (link.route && link.route !== this.$route.name) {
          this.$router.push({ name: link.route, params: { account: this.$route.params.account } })
        }

        !link.open ? link.open = true : null
        
        this.links.forEach((l) => { 
          if (l.name !== link.name) { l.open = false }
        })
      },

      hasPermission (accountPermissions, linkPermissions) {
        if (!accountPermissions) {
          return
        }
        
        if (this.account.role === 'admin' || this.account.role === 'owner' || this.account.role === 'partner') {
          return true
        }

        for (let perm of linkPermissions) {
          if (accountPermissions[perm]) {
            return true
          }
        }
        
        return false
      }
    },

    watch: {
      '$route' (newVal, oldVal) {
        if (newVal !== oldVal && this.open) {
          this.$emit('toggle')
        }
      }
    }
  }
</script>
